import { useMemo } from 'react';
import { Box, Link } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import { EmptyField } from 'components/elements/EmptyField';
import { Label } from 'components/elements/Label';
import { SkeletonProps } from 'components/elements/Skeleton';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useEventTriggerCategories } from 'state/eventTriggers';
import { LandTitleChange } from 'types/Documents';
import { City } from 'utils/constants/location_legacy';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { formatPrice } from 'utils/currency';
import { formatDate } from 'utils/date';
import { capitalizeWords } from 'utils/format';
import { cityMap } from 'utils/misc';
import type { Document } from 'types';

type Props = {
  result: Document<LandTitleChange> | null | undefined;
  skeleton: (props: Omit<SkeletonProps, 'isLoaded'>) => JSX.Element;
  isLoading?: boolean;
};

const LandTitleSection = ({ result, skeleton: Skeleton, isLoading = false }: Props) => {
  const { data: allCategories } = useEventTriggerCategories([cityMap[City.AUSTIN]], [9]);

  if (!result && !isLoading) return null;

  const capitalizationStyle = {
    textTransform: 'capitalize',
  };

  // Find the category name from the category id
  const categoryName = allCategories?.find(
    (category) => category.name === result?.metadata?.category,
  )?.name;

  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(result?.metadata?.relatedcontacts || []),
    [result?.metadata],
  );

  return (
    <Box>
      <MasterDocumentModal
        address={result?.metadata?.property_address_full ?? ''}
        city={result?.city ?? ''}
        state_code={result?.state_code ?? ''}
        isLoading={isLoading}
      />
      <DocumentRelatedCompanies
        contacts={relatedContacts}
        companies={result?.companies}
        documentId={result?.id}
        prefetchedAiContactSuggestionsByRelatedContactId={
          result?.ai_related_contact_suggestions_by_contact_id
        }
        Skeleton={Skeleton}
      />
      <InfoModal.Row>
        <Label>Land Owner</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={1}>
            <FlexColumn>
              {result?.companies && result?.companies?.length > 0 ? (
                result.companies?.map((company) => {
                  if (company.name) {
                    const companyName = capitalizeWords(company.name);

                    return company.uuid ? (
                      <Link href={`/companies/${company.uuid}`} key={company.uuid}>
                        <Text.BodyBold>{companyName}</Text.BodyBold>
                      </Link>
                    ) : (
                      <Text.Body key={company.name}>{companyName}</Text.Body>
                    );
                  }

                  return null;
                })
              ) : (
                <EmptyField />
              )}
            </FlexColumn>
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row hideDivider>
        <Label>Owner Address</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={1}>
            {result?.metadata?.buyer_mail_address_full ? (
              <Text sx={capitalizationStyle}>
                {result?.metadata?.buyer_mail_address_full}
              </Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Proposed Use</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={1}>
            {result?.metadata?.property_use ? (
              <Text sx={capitalizationStyle}>{result?.metadata?.property_use}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row hideDivider>
        <Label>Category</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={1}>
            {categoryName ? (
              <Text sx={capitalizationStyle}>{categoryName}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row hideDivider>
        <Label>Assessment Value</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={1}>
            {result?.metadata?.property_value ? (
              <Text>{formatPrice(result?.metadata?.property_value)}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row hideDivider>
        <Label>Unit Number</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={1}>
            {result?.metadata?.parcel_number ? (
              <Text sx={capitalizationStyle}>{result?.metadata?.parcel_number}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Title Number</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={1}>
            {result?.metadata?.title_number ? (
              <Text>{result.metadata.title_number}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row hideDivider>
        <Label>Date</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={1}>
            {result?.metadata?.issueddate ? (
              <Text>
                {formatDate(String(result?.metadata?.issueddate).toUpperCase())}
              </Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
    </Box>
  );
};

export default LandTitleSection;
